/* eslint-disable no-restricted-imports */
import { Box, Card, CardContent, Chip, Grid, LinearProgress, Typography } from '@material-ui/core';
import {
  Category as ItemIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as OrderIcon,
  TrendingUp as TrendingIcon,
} from '@material-ui/icons';

import { FormatMoney } from 'components';
import { STATUS_COLORS, STATUS_LABELS } from 'components/Contigo/constants';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export default function SummaryTab({ data, classes }) {
  const { summary } = data;

  const profit = summary.total_value - summary.total_cost;
  const margin = summary.total_value > 0 ? (profit / summary.total_value) * 100 : 0;

  const statusChartData = Object.entries(summary.by_status).map(([status, statusData]) => {
    const colorObj = STATUS_COLORS[status];
    const color = colorObj?.text || '#757575';
    return {
      name: STATUS_LABELS[status] || status,
      count: statusData.count,
      value: statusData.value,
      color,
    };
  });

  const financialChartData = Object.entries(summary.by_status)
    .map(([status, statusData]) => {
      const orderItems = data.detailed.filter((order) => order.status === status);
      const cost = orderItems.reduce((sum, order) => sum + order.total_cost, 0);

      return {
        name: STATUS_LABELS[status] || status,
        Receita: statusData.value,
        Custo: cost,
        Lucro: statusData.value - cost,
      };
    })
    .filter((item) => item.Receita > 0 || item.Custo > 0);

  const summaryCards = [
    {
      icon: <OrderIcon style={{ fontSize: 40, color: '#1976D2' }} />,
      label: 'Total de Pedidos',
      value: summary.total_orders,
      color: '#1976D2',
    },
    {
      icon: <ItemIcon style={{ fontSize: 40, color: '#7B1FA2' }} />,
      label: 'Total de Itens',
      value: summary.total_items,
      color: '#7B1FA2',
    },
    {
      icon: <MoneyIcon style={{ fontSize: 40, color: '#2E7D32' }} />,
      label: 'Valor Total',
      value: <FormatMoney>{summary.total_value}</FormatMoney>,
      color: '#2E7D32',
    },
    {
      icon: <TrendingIcon style={{ fontSize: 40, color: profit >= 0 ? '#2E7D32' : '#D32F2F' }} />,
      label: 'Lucro',
      value: <FormatMoney>{profit}</FormatMoney>,
      subtitle: `Margem: ${margin.toFixed(2)}%`,
      color: profit >= 0 ? '#2E7D32' : '#D32F2F',
    },
  ];

  return (
    <Box>
      {/* Cards de resumo */}
      <Grid container spacing={3}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Box display='flex' alignItems='center' justifyContent='space-between'>
                  {card.icon}
                  <Box textAlign='right'>
                    <Typography className={classes.summaryValue} style={{ color: card.color }}>
                      {card.value}
                    </Typography>
                    <Typography className={classes.summaryLabel}>{card.label}</Typography>
                    {card.subtitle && (
                      <Typography variant='caption' style={{ color: card.color }}>
                        {card.subtitle}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Gráficos */}
      <Box style={{ marginTop: 32 }}>
        <Grid container spacing={3}>
          {/* Gráfico de Pizza - Distribuição por Status */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Distribuição de Pedidos por Status
                </Typography>
                <ResponsiveContainer width='100%' height={300}>
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      dataKey='count'
                      nameKey='name'
                      cx='50%'
                      cy='50%'
                      outerRadius={100}
                      label
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Gráfico de Barras - Análise Financeira por Status */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Análise Financeira por Status
                </Typography>
                <ResponsiveContainer width='100%' height={300}>
                  <BarChart data={financialChartData}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='name' angle={-45} textAnchor='end' height={100} />
                    <YAxis tickFormatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`} />
                    <Tooltip
                      formatter={(value) =>
                        new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                        }).format(value)
                      }
                    />
                    <Legend />
                    <Bar dataKey='Receita' fill='#2E7D32' />
                    <Bar dataKey='Custo' fill='#F57C00' />
                    <Bar dataKey='Lucro' fill='#1976D2' />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Pedidos por Status */}
      <Box className={classes.statusGrid}>
        <Typography variant='h6' gutterBottom>
          Pedidos por Status
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(summary.by_status).map(([status, data]) => (
            <Grid item xs={12} sm={6} md={4} key={status}>
              <Card>
                <CardContent>
                  <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
                    <Chip
                      label={STATUS_LABELS[status] || status}
                      style={{
                        backgroundColor: STATUS_COLORS[status] || '#757575',
                        color: 'white',
                      }}
                      size='small'
                    />
                    <Typography variant='h6'>{data.count}</Typography>
                  </Box>
                  <Typography variant='body2' color='textSecondary'>
                    Valor Total
                  </Typography>
                  <Typography variant='h6' style={{ color: '#2E7D32' }}>
                    <FormatMoney>{data.value}</FormatMoney>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Totais consolidados com indicadores visuais */}
      <Card style={{ marginTop: 24 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>
            Análise Financeira Consolidada
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Typography variant='body2' color='textSecondary' gutterBottom>
                Receita Total
              </Typography>
              <Typography variant='h5' style={{ color: '#2E7D32', marginBottom: 8 }}>
                <FormatMoney>{summary.total_value}</FormatMoney>
              </Typography>
              <LinearProgress
                variant='determinate'
                value={100}
                style={{ height: 8, borderRadius: 4, backgroundColor: '#E8F5E9' }}
                classes={{
                  bar: { backgroundColor: '#2E7D32' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant='body2' color='textSecondary' gutterBottom>
                Custo Total
              </Typography>
              <Typography variant='h5' style={{ color: '#F57C00', marginBottom: 8 }}>
                <FormatMoney>{summary.total_cost}</FormatMoney>
              </Typography>
              <LinearProgress
                variant='determinate'
                value={
                  summary.total_value > 0 ? (summary.total_cost / summary.total_value) * 100 : 0
                }
                style={{ height: 8, borderRadius: 4, backgroundColor: '#FFF3E0' }}
                classes={{
                  bar: { backgroundColor: '#F57C00' },
                }}
              />
              <Typography variant='caption' color='textSecondary'>
                {summary.total_value > 0
                  ? `${((summary.total_cost / summary.total_value) * 100).toFixed(1)}% da receita`
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant='body2' color='textSecondary' gutterBottom>
                Lucro Líquido
              </Typography>
              <Typography
                variant='h5'
                style={{ color: profit >= 0 ? '#2E7D32' : '#D32F2F', marginBottom: 8 }}
              >
                <FormatMoney>{profit}</FormatMoney>
              </Typography>
              <LinearProgress
                variant='determinate'
                value={Math.min(Math.abs(margin), 100)}
                style={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: profit >= 0 ? '#E8F5E9' : '#FFEBEE',
                }}
                classes={{
                  bar: { backgroundColor: profit >= 0 ? '#2E7D32' : '#D32F2F' },
                }}
              />
              <Typography
                variant='caption'
                style={{ color: profit >= 0 ? '#2E7D32' : '#D32F2F', fontWeight: 600 }}
              >
                Margem: {margin.toFixed(2)}%
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
